import { CabButton } from "@CabComponents/CabButton";
import { CabModal } from "@CabComponents/CabModal";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { Box, FormControl, FormLabel } from "@mui/material";
import { ReactElement, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { emailRegex } from "../../constants";
import { ContactRequest, ContactType, Tag } from "../../store/cabinetApi/generated/crm";
import { CabDropdown } from "@CabComponents/CabDropdown";
import colors from "../../colors";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { CabExecPicker } from "@CabComponents/CabExecPicker";
import { Leader } from "../../store";
import CabAvatar from "@CabComponents/CabAvatar";
import { getLeaderIconSrc } from "../../utils/leaderUtils";



interface NewContactModalProps {
  open: boolean;
  onClose: () => void;
  contactTags: Tag[];
  contactTypes: ContactType[];
  onCreateContact: (contact: ContactRequest, leaderIds?: Leader['id'][]) => Promise<void>;
  isCreatingContact: boolean;
  leaderOptions: Leader[];
}

interface FormInput {
  name: string;
  email: string;
  title: string;
  tags?: number[];
  contactType?: number;
  relationshipLeaders?: Leader['id'][];
}

const NewContactModal = ({
  open, onClose, contactTags, contactTypes, onCreateContact, isCreatingContact, leaderOptions
}: NewContactModalProps): ReactElement => {

  const defaultValues: FormInput = {
    name: '',
    email: '',
    title: '',
    tags: [],
    contactType: -1,
    relationshipLeaders: [],
  };

  const contactForm = useForm<FormInput>({
    defaultValues: defaultValues
  });
  const { control, reset, handleSubmit: formSubmit, formState: { isValid } } = contactForm;

  const tagOptions = contactTags;
  // const typeOptions = [{id: -1, name: "None", color: colors.white800}, ...contactTypes];

  const cabExecOptions = useMemo(() => leaderOptions.map(leader => {
    return {
      value: leader.id,
      label: `${leader.first_name} ${leader.last_name}`,
      icon: <CabAvatar
        src={getLeaderIconSrc(leader)}
        color={leader.color}
        name={`${leader.first_name}
        ${leader.last_name}`}
        size="small"
      />
    };
  }), [leaderOptions]);

  const handleSubmit = async (v: FormInput) => {
    await onCreateContact({
      name: v.name,
      primary_email: v.email,
      title: v.title,
      tags: v.tags && v.tags.length > 0 ? v.tags : undefined,
      contact_type: v.contactType && v.contactType > 0 ? v.contactType : undefined,
      fields: {},
    }, v.relationshipLeaders);
    reset();
    onClose();
  };

  return (
    <CabModal
      open={open}
      onClose={onClose}
      title={'Create New Person'}
      closeIcon
      actionButtons={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
          }}>
          <CabButton color='error' onClick={() => reset()}>
            Discard
          </CabButton>
          <Box
            sx={{
              display: 'flex',
              gap: 1
            }}>
            <CabButton buttonType="secondary" onClick={onClose}>
              Cancel
            </CabButton>
            <CabButton 
              disabled={!isValid || isCreatingContact} 
              onClick={formSubmit(handleSubmit)}
            >
              Save
            </CabButton>
          </Box>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%'
        }}>
        <FormControl>
          <FormLabel>Name*</FormLabel>
          <Controller name='name' control={control}
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <CabTextInput
                {...field}
                inputRef={ref}
                required
              />
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Email*</FormLabel>
          <Controller 
            name='email' control={control}
            rules={{ required: true, pattern: emailRegex }}
            render={({ field: { ref, ...field } }) => (
              <CabTextInput
                {...field}
                type='email'
                inputRef={ref}
                required
              />
            )}/>
        </FormControl>
        <FormControl>
          <FormLabel>Title</FormLabel>
          <Controller name='title' control={control}
            render={({ field: { ref, ...field } }) => (
              <CabTextInput
                {...field}
                inputRef={ref}
                required
              />
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Tags</FormLabel>
          <Controller name='tags' control={control}
            render={({ field: { onChange, value } }) => (
              <CabDropdown<number>
                multiple
                value={value || []}
                onChange={(e) => {
                  const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
                  onChange(selectedValues.map(v => Number(v)).filter(v => !isNaN(v)));
                }}
                options={tagOptions.map(option => ({
                  value: option.id,
                  label: <CabToggleChip chipColor={option.color || colors.white800} label={option.name} 
                    selected={true}/>
                }))}
                placeholder="Select tags..."
                overrides={{
                  renderValue: (selectedValue: unknown) => {
                    const selectedIds = Array.isArray(selectedValue) ? selectedValue : [];
                    if (selectedIds.length === 0) {
                      return <Box component="span" sx={{ color: colors.black400 }}>Select tags...</Box>;
                    }
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5
                        }}>
                        {selectedIds.map((tagId: number) => {
                          const tag = tagOptions.find(t => t.id === tagId);
                          if (!tag) return null;
                          return (
                            <CabToggleChip
                              key={tag.id}
                              chipColor={tag.color || colors.white800}
                              label={tag.name}
                              selected={true}
                            />
                          );
                        })}
                      </Box>
                    );
                  }
                }}
              />
            )}
          />
        </FormControl>
        {/* <FormControl>
          <FormLabel>Contact Type</FormLabel>
          <Controller name='contactType' control={control}
            render={({ field: { onChange, value } }) => (
              <CabDropdown<number>
                value={value || -1}
                onChange={onChange}
                options={typeOptions.map(option => ({
                  value: option.id,
                  label: <CabToggleChip chipColor={option.color || colors.white800} label={option.name} 
                    selected={true}/>
                }))}
              />
            )}
          />
        </FormControl> */}

        <FormControl sx={{ marginTop: 1 }}>
          <FormLabel>Relationships</FormLabel>
          <Controller name="relationshipLeaders" control={control}
            render={({ field: { ref, ...field } }) => (
              <CabExecPicker
                fullWidth
                multiple
                {...field}
                options={cabExecOptions}
                placeholder="Select a Teammate"
                sx={{ marginTop: '0px !important' }}
              />
            )}
          />
        </FormControl>

      </Box>
    </CabModal>
  );
};

export default NewContactModal;